import { Container, IDestroyOptions, Point, Sprite } from "pixi.js";
import { BasePageView } from "../../../../common/components/BasePageView";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { GiftResourcePackage } from "../../resources/GiftResourcePackage";
import { GiftBox } from "../../components/GiftBox";
import { gsap } from "gsap";
import { TickerDelay } from "../../../../../../tools/TickerDelay";
import { EmitterConfigV2 } from "@pixi/particle-emitter";
import { ConfetyParticleAnimationView } from "../../components/particles/ConfetyParticleAnimationView";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { DiceComboAction } from "../../../../game/flow/impl/diceActions/DiceComboAction";
import { GiftShieldsAction } from "../../../../game/flow/impl/diceActions/GiftShieldsAction";

export class GiftBoxPage extends BasePageView {
    private static readonly GIFT_BOXES_CONTAINER_SCALE = 0.55;
    private static readonly SELECT_GIFT_BOX_ANIMATION_TIME = 0.5;

    private readonly giftResources: GiftResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly action: DiceComboAction;
    private fadeScreen: FadeScreen;
    private glow: Sprite;
    private giftBoxesContainer: Container;
    private giftBoxes: Array<GiftBox>;
    private boxMoveStartPosition: Point;
    private selectBoxMovePosition: Point;
    private viewCompletionSource: PromiseCompletionSource<void>;
    private showOtherBoxesAnimation: gsap.core.Timeline;
    private currentAnimation: gsap.core.Timeline;
    private particleAnimation: ConfetyParticleAnimationView;

    public constructor(
        giftResources: GiftResourcePackage,
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        action: DiceComboAction,
        viewCompletionSource: PromiseCompletionSource<void>
    ) {
        super(canvasScaler);
        this.giftResources = giftResources;
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.action = action;
        this.viewCompletionSource = viewCompletionSource;
        this.giftBoxes = [];
        this.build();
        this.once("added", this.showComponents.bind(this));
        this.eventMode = "static";
        this.on("pointerup", this.skipAnimations, this);
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.glow = new Sprite(this.giftResources.assets_views_gift_ui_light_glow_png);
        this.glow.anchor.set(0.5, 0.5);
        this.addChild(this.glow);
        this.giftBoxesContainer = new Container();
        this.giftBoxesContainer.scale.set(GiftBoxPage.GIFT_BOXES_CONTAINER_SCALE);
        this.addChild(this.giftBoxesContainer);
        this.giftBoxesContainer.addChild(this.giftBoxFactory());
        this.giftBoxesContainer.addChild(this.giftBoxFactory());
        this.giftBoxesContainer.addChild(this.giftBoxFactory());
        super.build();
    }

    private giftBoxFactory(): GiftBox {
        const giftBox = new GiftBox(this.giftResources);
        this.giftBoxes.push(giftBox);
        return giftBox;
    }

    private async showComponents(): Promise<void> {
        this.interactiveBoxes(false);
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        this.fadeScreen.fadeIn();
        animationTimeLine.addLabel("toolTipShow");
        this.giftBoxes.forEach(giftBox => {
            animationTimeLine.fromTo(giftBox, { x: this.boxMoveStartPosition.x, y: this.boxMoveStartPosition.y, angle: giftBox.angle * 4 }, {
                x: giftBox.x,
                y: giftBox.y,
                angle: giftBox.angle,
                duration: 0.6,
                ease: "back.out(1)",
                onStart: () => this.soundResources.assets_sounds_Popups_Entering_mp3.play()
            }, "<30%");
        });
        await animationTimeLine;
        animationTimeLine.kill();
        await TickerDelay.delay(100);
        await this.selectBox(this.giftBoxes[1]);
    }

    private async selectBox(giftBox: GiftBox): Promise<void> {
        this.interactiveBoxes(false);
        const config = this.giftResources.assets_views_gift_emittor_emitter_confety_json as EmitterConfigV2;
        this.particleAnimation = new ConfetyParticleAnimationView(config, [
            this.commonResources.assets_common_ui_confety_confetti_3_png,
            this.commonResources.assets_common_ui_confety_confetti_4_png,
            this.commonResources.assets_common_ui_confety_confetti_5_png,
            this.commonResources.assets_common_ui_confety_confetti_9_png,
            this.commonResources.assets_common_ui_confety_confetti_10_png,
        ]);
        this.particleAnimation.scale.set(1.3);
        this.particleAnimation.position.set(this.giftBoxesContainer.position.x, this.giftBoxesContainer.position.y + 220);
        this.addChild(this.particleAnimation);
        await this.selectGiftBoxAnimation(giftBox);
        await TickerDelay.delay(this.action.constructor == GiftShieldsAction ? 400 : 700);
        this.viewCompletionSource.complete();
        await this.showOtherBoxes(giftBox);
    }

    private async selectGiftBoxAnimation(giftBox: GiftBox): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.to(giftBox, { y: giftBox.y + 20, x: giftBox.x + 20 * -giftBox.rotation, duration: 0.1, yoyo: true, repeat: 1 });
        animationTimeLine.to(giftBox.scale, { x: 0.95, y: 0.95, duration: 0.1, yoyo: true, repeat: 1 }, "<");
        animationTimeLine.addLabel("selectedBox");
        const boxIndex = this.giftBoxesContainer.getChildIndex(giftBox);
        this.giftBoxesContainer.setChildIndex(giftBox, this.giftBoxes.length - 1);
        if (boxIndex !== 1) {
            const switchedBox = this.giftBoxes[1];
            animationTimeLine.to(switchedBox, { angle: giftBox.angle, duration: GiftBoxPage.SELECT_GIFT_BOX_ANIMATION_TIME, ease: "back.in(5)" });
            animationTimeLine.to(switchedBox, { x: giftBox.x, y: giftBox.y, delay: GiftBoxPage.SELECT_GIFT_BOX_ANIMATION_TIME * 0.5, duration: GiftBoxPage.SELECT_GIFT_BOX_ANIMATION_TIME * 0.5, ease: "power0.in" }, "<");
        }
        this.soundResources.assets_sounds_Card_Zoom_In_mp3.play();
        animationTimeLine.add(giftBox.hideBoxGlowTween(), "selectedBox");
        animationTimeLine.to(giftBox, { angle: -30, duration: GiftBoxPage.SELECT_GIFT_BOX_ANIMATION_TIME * 0.4, ease: "none" }, "selectedBox");
        animationTimeLine.to(giftBox, { x: this.selectBoxMovePosition.x, y: this.selectBoxMovePosition.y, duration: GiftBoxPage.SELECT_GIFT_BOX_ANIMATION_TIME * 0.8, ease: "power0.in" }, "<");
        animationTimeLine.to(giftBox.scale, { x: 1.4, y: 1.4, duration: GiftBoxPage.SELECT_GIFT_BOX_ANIMATION_TIME * 0.8, ease: "power0.in" }, "<");
        animationTimeLine.to(giftBox, { angle: -20, duration: GiftBoxPage.SELECT_GIFT_BOX_ANIMATION_TIME * 0.4, ease: "none" }, "<50%");
        const giftBoxOpenAnimation = giftBox.openBoxAnimation();
        giftBoxOpenAnimation.add(() => this.particleAnimation.play(), "<85%");
        animationTimeLine.add(giftBoxOpenAnimation, "<90%")
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private async showOtherBoxes(selectedBox: GiftBox): Promise<void> {
        this.showOtherBoxesAnimation = new gsap.core.Timeline();
        await this.showOtherBoxesAnimation.addPause("+=0.2");
        this.showOtherBoxesAnimation.addLabel("boxMove");
        this.showOtherBoxesAnimation.addLabel("doneMove", 0.8);
        this.giftBoxes.forEach(giftBox => {
            if (giftBox != selectedBox) {
                this.showOtherBoxesAnimation.to(giftBox, { y: giftBox.y - giftBox.height * 0.3, duration: 0.4, ease: "none" }, "boxMove");
                this.showOtherBoxesAnimation.to(giftBox, { alpha: 0, duration: 0.4, ease: "none" }, "doneMove");
            }
        });
        await this.showOtherBoxesAnimation.play();
        this.showOtherBoxesAnimation.clear();
    }

    private interactiveBoxes(isLock: boolean): void {
        this.giftBoxes.forEach(giftBox => giftBox.eventMode = isLock ? "dynamic" : "none");
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
        if (this.particleAnimation != null && this.particleAnimation.isActive) {
            this.particleAnimation.setProgress(1);
        }
    }

    protected override layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.giftBoxesContainer.position.set(width * 0.5, height * 0.5);
        this.glow.position = this.giftBoxesContainer.position;
        this.giftBoxesLayout();
        this.boxMoveStartPosition = new Point(0, height);
        this.selectBoxMovePosition = new Point(0, height * 0.3);
        super.layout(width, height);
    }

    private giftBoxesLayout(): void {
        const boxAngle = 15;
        let boxIndex = -1;
        this.giftBoxes.forEach(giftBox => {
            giftBox.angle = boxAngle * boxIndex;
            giftBox.position.x = giftBox.width * 0.6 * boxIndex;
            giftBox.position.y = boxIndex === 0 ? giftBox.height * -0.1 : boxIndex === 1 ? giftBox.height * 0.1 : giftBox.height * 0.15;
            boxIndex++;
        })
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        this.particleAnimation.destroy();
        this.showOtherBoxesAnimation?.kill();
        super.destroy(options);
    }
}
